import { AiParseRcState } from "../enums/ai-parse-rc-state.enum";
import { AutoHoldType } from "../enums/auto-hold-type.enum";
import BusinessTier from "../enums/business-tier.enum";
import ClientTransitInfoDisplayLevel from "../enums/client-transit-info-display-level.enum";
import ContractStatus from "../enums/contract-status.enum";
import CostEnforcement from "../enums/cost-enforcement.enum";
import DriverAgreementFlow from "../enums/driver-agreement-flow.enum";
import { DefaultDriverPricingModel } from "../enums/driver-pricing-model.enum";
import LocationSource from "../enums/location-source.enum";
import MissingInfoDisplayLevel from "../enums/missing-info-display-level.enum";
import { NavigationSearchType } from "../enums/navigation-search-type.enum";
import QuickPayMode from "../enums/quick-pay-mode.enum";
import UserRole from "../enums/user-role.enum";
import { UserRoleWildcard } from "../enums/user-role-wildcard.enum";
import type { CreateOrganizationInput } from "../types/organization.type";

const DEFAULT_AUTO_ASSIGN_ROLES = [
  UserRole.OWNER,
  UserRole.LH_SUPPORT,
  UserRole.ADMIN,
  UserRole.MANAGER,
  UserRole.DISPATCHER,
];

/** Defaults aligned with the organizations DB migration. */
export function getDefaultCreateOrganizationInput(): CreateOrganizationInput {
  return {
    name: "",
    displayName: "",
    mcNumber: null,
    primaryColor: null,
    secondaryColor: null,
    tabColor: null,
    logoUrl: null,
    driverAgreementFile: "",
    terminationDate: null,
    hasOverdueBalance: false,
    lblTruckInsuranceDescription: "",
    publicCreateLoadPageTripDistanceEnabled: false,
    clientShowingLoadBooleans: true,
    clientShowingDriverEta: false,
    loadTierPremiumDomains: [],
    loadTierPremiumEmails: [],
    allowEnableAutoAssignUserRoles: [...DEFAULT_AUTO_ASSIGN_ROLES],
    allowDisableAutoAssignUserRoles: [...DEFAULT_AUTO_ASSIGN_ROLES],
    defaultLocationService: LocationSource.AWS,
    loadhiveSupportEmail: "loadhive@loadhive.com",
    driverSupportEmail: "",
    driverSupportPhone: "",
    disabledLoadSources: [],
    trackingContactEmail: "",
    autoOfferReplyToEmail: "",
    trackingContactPhone: "",
    hrEmail: "",
    clientShowPickupValues: false,
    clientShowDeliverValues: false,
    lhNumberPrefix: "",
    autoAssignToAcceptedDrivers: false,
    autoAssignByDeliverLocation: true,
    driverDeadheadEtaBufferMs: 0,
    loadExpireMs: 900_000,
    driverOfferExpireMs: 300_000,
    driverExtendOfferExpireTimeEnabled: false,
    dispatcherOfferExpireExtensionMs: 600_000,
    dispatcherOfferExpireMs: 900_000,
    driverHoldExpireDurationMs: 600_000,
    autoAssignLocationAgeMs: 432_000_000,
    defaultDriverPricingModel: DefaultDriverPricingModel.DRIVER_TRUCK_TYPE,
    allowLhNumberTransfers: false,
    todaysOfferStatsDisplayRoles: [UserRoleWildcard.ANY_ROLE],
    warningKeysRequiredForDriverAgreement: [
      "FIRST_NAME",
      "LAST_NAME",
      "EMAIL",
      "LICENSE",
      "PRIMARY_TRUCK_ID",
      "REGISTRATION",
      "INSURANCE",
      "TRUCK_IMAGES",
    ],
    autoAssignDollarsCeilingStep: 1000,
    autoAssignEnabled: false,
    autoAssignMaxLoadMiles: null,
    maxAutoAssignDistanceMiles: 250,
    autoAssignDistanceMiles: 120,
    autoAssignCopyLoadNotesToOffer: false,
    autoAssignToInTransitDrivers: false,
    autoAssignInTransitDriverPercentage: 90,
    autoAssignWhenNoTruckType: false,
    autoAssignPriceNegotiable: true,
    autoAssignOnHoldDrivers: false,
    allowDriverRecordEntry: false,
    allowPpeEntry: false,
    extraApiCorsDomains: [],
    pickupAllowPhotoLibrary: false,
    dropOffAllowPhotoLibrary: false,
    publicTrucksMapEnabled: false,
    publicTrucksMapToken: "",
    navigationSearchType: NavigationSearchType.ADDRESS_PLUS_BUSINESS_NAME,
    publicCreateLoadPageEnabled: false,
    publicCreateLoadPageAutoAssign: false,
    allowDriverSetAutoAssignRadius: true,
    allowDriverSetAutoAssignMaxMiles: true,
    allowDriverChangePhoneNumber: true,
    driverAutoAssignCanadaDefault: false,
    requireAddressToAcceptOffer: true,
    profitPercent: 0,
    profitDollarsCeilingStep: 100,
    minProfitDollarAmount: 0,
    atPickupStatusEnabled: false,
    atDropOffStatusEnabled: false,
    canadaLoadsEnabled: false,
    slackBusinessWebhook: null,
    aiParseRc: AiParseRcState.MANUAL,
    accountingIncludeTonuOffers: false,
    disablePinpoint: false,
    tier: BusinessTier.BASIC,
    redirectUrlPrivacyPolicy: null,
    redirectUrlTermsOfService: null,
    redirectUrlEula: null,
    redirectUrlIosAppStore: null,
    redirectUrlAndroidPlayStore: null,
    redirectUrlDriverAgreement: null,
    redirectUrlDriverAgreementPreview: null,
    quickPayMode: QuickPayMode.OFF,
    quickPaySupported: false,
    quickPayPercent: 0,
    costEnforcement: CostEnforcement.NONE,
    driverHoldOnReply: AutoHoldType.OFF,
    driverMissingPaymentInfoDisplayLevel: MissingInfoDisplayLevel.NONE,
    clientShowingPickupDetails: ClientTransitInfoDisplayLevel.NONE,
    clientShowingDeliverDetails: ClientTransitInfoDisplayLevel.VALUES_AND_FILES,
    driverDefaultContractStatus: ContractStatus.PENDING_DRIVER,
    driverAgreementFlow: DriverAgreementFlow.INTEGRATED,
    driverAgreementClientUserId: null,
    emailTemplatesAssetUrl: "",
    offerSubscriptionsAutoEmailsEnabled: false,
    offerSubscriptionsOnAcceptedDefault: false,
    offerSubscriptionsOnAtPickupDefault: false,
    offerSubscriptionsOnInTransitDefault: false,
    offerSubscriptionsOnAtDropOffDefault: false,
    offerSubscriptionsOnDeliveredDefault: false,
    pickupAllowShipperNameEntry: false,
    allowTruckSideDoorEntry: false,
    allowTruckBackDoorEntry: false,
    allowTruckMcNumberEntry: false,
    offerPodIncludeCoverPageDefault: false,
  };
}
